import { _ as _assign } from '../tslib.es6-c6da0ae6.js';
import { jsxs, jsx } from 'react/jsx-runtime';
import { useTranslator } from '@picsart/rc/services/localization';
import { HorizontalContainer } from '@picsart/design-system/components/HorizontalContainer';
import classNames from 'classnames';
import usePageSlug from '../usePageSlug/index.js';
import { T as Title } from '../index-6f49f05f.js';
import { TextColors, SecondaryColors, BackgroundColors, PositiveColors, Gradients } from '@picsart/design-system/foundation/colors';
import { FontSizes, LineHeights } from '@picsart/design-system/foundation/typography';
import { createUseStyles } from 'react-jss';
import { Spacings } from '@picsart/design-system/foundation/spacings';
import { FontWeights } from '@picsart/design-system/foundation/fonts';
import { OldBreakPoints } from '@picsart/rc/enums';
import { CornerRadius } from '@picsart/design-system/foundation/cornerRadius';
import { BreakPoints } from '@picsart/design-system/foundation/responsive';
import { mediaQuery } from '../mediaHelper/index.js';

var data = {
  items: [{
    title: '1B+',
    description: 'creative edits made every month'
  }, {
    title: '1M+',
    description: 'AI images generated daily'
  }, {
    title: '20+',
    description: 'generative AI tools to transform your workflow'
  }, {
    title: '300M+',
    description: 'editable photos, templates, and more'
  }]
};

var _a, _b, _c;
var useStyles = createUseStyles({
  topSection: {
    textAlign: 'center',
    '& > h2': {
      color: TextColors.base,
      maxWidth: '80%',
      margin: [Spacings.s0, 'auto'],
      '& i': {
        color: function color(_a) {
          var titleColor = _a.titleColor;
          return titleColor || SecondaryColors.base["default"];
        },
        fontStyle: 'normal'
      }
    }
  },
  subtitle: {
    fontSize: FontSizes.s16,
    color: TextColors.tint1,
    lineHeight: LineHeights.l24,
    fontWeight: FontWeights.semibold
  },
  content: (_a = {
    display: 'flex',
    justifyContent: 'center',
    flexWrap: 'wrap',
    gap: Spacings.s24,
    marginTop: Spacings.s40
  }, _a["@media screen and (max-width: ".concat(OldBreakPoints.xsmall, "px)")] = {
    flexDirection: 'column',
    alignItems: 'center',
    gap: Spacings.s32
  }, _a),
  item: (_b = {
    display: 'grid',
    gridTemplateRows: '1fr 1fr',
    backgroundColor: BackgroundColors.tint2,
    justifyContent: 'center',
    width: 272,
    height: 276,
    borderRadius: CornerRadius.r8,
    textAlign: 'center',
    padding: [Spacings.s0, Spacings.s24]
  }, _b[mediaQuery(BreakPoints.Small)] = {
    display: 'none'
  }, _b["@media screen and (max-width: ".concat(OldBreakPoints.medium, "px)")] = {
    height: 190,
    width: '47%'
  }, _b["@media screen and (max-width: ".concat(OldBreakPoints.xlsmall, "px)")] = {
    height: 'inherit',
    width: 270,
    backgroundColor: PositiveColors.base["default"],
    padding: 0
  }, _b['& > strong'] = {
    fontWeight: FontWeights.semibold
  }, _b['&:nth-child(1) > strong'] = {
    backgroundClip: 'text',
    '-webkit-background-clip': 'text',
    '-webkit-text-fill-color': 'transparent',
    background: Gradients.primary
  }, _b['&:nth-child(2) > strong'] = {
    backgroundClip: 'text',
    '-webkit-background-clip': 'text',
    '-webkit-text-fill-color': 'transparent',
    background: Gradients.gold
  }, _b['&:nth-child(3) > strong'] = {
    backgroundClip: 'text',
    '-webkit-background-clip': 'text',
    '-webkit-text-fill-color': 'transparent',
    background: Gradients.midnight
  }, _b['&:nth-child(4) > strong'] = {
    backgroundClip: 'text',
    '-webkit-background-clip': 'text',
    '-webkit-text-fill-color': 'transparent',
    background: Gradients.pro
  }, _b),
  itemDescription: (_c = {
    fontSize: FontSizes.s20,
    fontWeight: FontWeights.semibold,
    lineHeight: LineHeights.l28
  }, _c[mediaQuery(BreakPoints.Small)] = {
    margin: [Spacings.s0, Spacings.s4]
  }, _c),
  itemTitle: function itemTitle(props) {
    var _a;
    return _a = {
      fontSize: (props === null || props === void 0 ? void 0 : props.isLocalized) ? FontSizes.s42 : 70,
      alignSelf: 'end',
      whiteSpace: 'nowrap'
    }, _a["@media screen and (max-width: ".concat(OldBreakPoints.large, "px)")] = {
      fontSize: (props === null || props === void 0 ? void 0 : props.isLocalized) ? FontSizes.s36 : 70
    }, _a;
  },
  skeletonWrapper: {
    display: 'flex',
    alignItems: 'center',
    flexDirection: 'column',
    marginTop: Spacings.s72
  },
  skeletonExcerpt: {
    height: 14,
    width: 216
  },
  skeletonTitle: {
    height: 144,
    maxWidth: '80%',
    marginTop: Spacings.s24
  },
  skeletonContent: {
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'center',
    marginTop: Spacings.s40,
    gap: 24
  },
  skeletonItem: {
    width: 272,
    height: 276
  }
}, {
  name: 'Trustbox'
});

var TrustBox = function TrustBox(_a) {
  var subtitle = _a.subtitle,
    title = _a.title,
    _b = _a.items,
    items = _b === void 0 ? data.items : _b,
    className = _a.className;
  var translate = useTranslator();
  var languageParam = usePageSlug().languageParam;
  var styles = useStyles({
    isLocalized: languageParam !== '',
    titleColor: title === null || title === void 0 ? void 0 : title.color
  });
  return jsxs(HorizontalContainer, {
    children: [jsxs("div", _assign({
      className: classNames(styles.topSection, className),
      "data-pulse-section": "trustBox-top-section",
      "data-testid": "main"
    }, {
      children: [subtitle && jsx("p", _assign({
        className: styles.subtitle
      }, {
        children: translate(subtitle)
      })), title && jsx(Title, _assign({}, title))]
    })), jsx("div", _assign({
      className: styles.content,
      "data-pulse-section": "trustBox-item-section"
    }, {
      children: items === null || items === void 0 ? void 0 : items.map(function (item, index) {
        return jsxs("div", _assign({
          className: styles.item
        }, {
          children: [jsx("strong", _assign({
            className: styles.itemTitle
          }, {
            children: translate(item.title)
          })), jsx("p", _assign({
            className: styles.itemDescription
          }, {
            children: translate(item.description)
          }))]
        }), index);
      })
    }))]
  });
};

var TrustBoxFallback = function TrustBoxFallback() {
  var styles = useStyles({
    titleColor: ''
  });
  return jsxs(HorizontalContainer, {
    children: [jsxs("i", _assign({
      "data-testid": "main",
      className: styles.skeletonWrapper
    }, {
      children: [jsx("i", {
        className: classNames('skeleton', styles.skeletonExcerpt)
      }), jsx("i", {
        className: classNames('skeleton', styles.skeletonTitle)
      })]
    })), jsxs("i", _assign({
      className: styles.skeletonContent
    }, {
      children: [jsx("i", {
        className: classNames('skeleton', styles.skeletonItem)
      }), jsx("i", {
        className: classNames('skeleton', styles.skeletonItem)
      }), jsx("i", {
        className: classNames('skeleton', styles.skeletonItem)
      }), jsx("i", {
        className: classNames('skeleton', styles.skeletonItem)
      })]
    }))]
  });
};

export { TrustBoxFallback, TrustBox as default };
//# sourceMappingURL=index.js.map
